import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import { cardStyle, CardDecorations, headingStyle, spinnerStyle, CollapsibleCard } from './dashboardStyles';

interface TransitAspect {
  transitPlanet: string;
  transitPlanetRu?: string;
  natalPlanet: string;
  natalPlanetRu?: string;
  aspectType: string;
  orb?: number;
  strength?: number;
  applying?: boolean;
}

interface TransitsData {
  transitAspects?: TransitAspect[];
}

const PLANET_SYMBOLS: Record<string, string> = {
  Sun: '☉', Moon: '☽', Mercury: '☿', Venus: '♀', Mars: '♂',
  Jupiter: '♃', Saturn: '♄', Uranus: '♅', Neptune: '♆', Pluto: '♇',
  Chiron: '⚷', Lilith: '⚸', NorthNode: '☊', SouthNode: '☋',
};

const ASPECT_DESCRIPTIONS: Record<string, string> = {
  conjunction: 'Слияние энергий — усиление обеих планет',
  opposition: 'Противостояние — необходимость баланса',
  trine: 'Гармония — лёгкость и поддержка',
  square: 'Напряжение — стимул к действию',
  sextile: 'Возможность — шанс для развития',
};

function getAspectDescription(aspectType: string): string {
  const lower = aspectType.toLowerCase();
  for (const [key, desc] of Object.entries(ASPECT_DESCRIPTIONS)) {
    if (lower.includes(key)) return desc;
  }
  return 'Астрологический аспект';
}

const HARMONIOUS_ASPECTS = ['trine', 'sextile', 'conjunction', 'трин', 'секстиль', 'соединение'];
const TENSE_ASPECTS = ['square', 'opposition', 'квадрат', 'оппозиция'];

function isHarmonious(aspect: string): boolean {
  const lower = aspect.toLowerCase();
  return HARMONIOUS_ASPECTS.some(a => lower.includes(a));
}

function isTense(aspect: string): boolean {
  const lower = aspect.toLowerCase();
  return TENSE_ASPECTS.some(a => lower.includes(a));
}

function getAspectColor(aspect: string): string {
  if (isHarmonious(aspect)) return '#4CAF50';
  if (isTense(aspect)) return '#ef5350';
  return '#b0b0b0';
}

function getAspectEmoji(aspect: string): string {
  if (isHarmonious(aspect)) return '✅';
  if (isTense(aspect)) return '⚠️';
  return '🔹';
}

const DashboardTransits: React.FC = () => {
  const { user } = useAuth();
  const [transits, setTransits] = useState<TransitAspect[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchTransits = async () => {
      if (!user?.id) {
        console.log('[DashboardTransits] No user ID');
        setLoading(false);
        return;
      }
      try {
        console.log('[DashboardTransits] Fetching transits for user:', user.id);
        const res = await api.get<TransitsData>(`/calculations/transits/${user.id}`);
        console.log('[DashboardTransits] API response:', res);
        if (res.success && res.data) {
          const list = res.data.transitAspects || [];
          console.log('[DashboardTransits] Transit aspects count:', list.length);
          setTransits(list.slice(0, 5));
        } else {
          console.log('[DashboardTransits] API returned success=false or no data');
        }
      } catch (error) {
        console.error('[DashboardTransits] Error fetching transits:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransits();
  }, [user?.id]);

  if (loading) {
    return (
      <CollapsibleCard title="🔄 Текущие транзиты" marginBottom="24px" padding={isMobile ? '16px 12px' : '20px'}>
        <div style={spinnerStyle}>🔄 Загрузка транзитов...</div>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard title="🔄 Текущие транзиты" marginBottom="24px" padding={isMobile ? '16px 12px' : '20px'}>
      {transits.length === 0 ? (
        <p style={{ color: '#888', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>
          Нет данных о текущих транзитах
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {transits.map((transit, idx) => {
            const color = getAspectColor(transit.aspectType);
            const emoji = getAspectEmoji(transit.aspectType);
            const transitingName = transit.transitPlanetRu || transit.transitPlanet;
            const natalName = transit.natalPlanetRu || transit.natalPlanet;
            const transitingSymbol = PLANET_SYMBOLS[transit.transitPlanet] || '✦';
            const natalSymbol = PLANET_SYMBOLS[transit.natalPlanet] || '✦';
            const desc = getAspectDescription(transit.aspectType);

            return (
              <div
                key={idx}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px',
                  padding: '10px 14px',
                  borderLeft: `3px solid ${color}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  <span style={{ fontSize: '16px' }}>{transitingSymbol}</span>
                  <span style={{ color: '#e0e0e0', fontSize: '13px', fontWeight: 500 }}>{transitingName}</span>
                  <span style={{ color, fontSize: '11px', fontWeight: 600 }}>{transit.aspectType}</span>
                  <span style={{ fontSize: '16px' }}>{natalSymbol}</span>
                  <span style={{ color: '#e0e0e0', fontSize: '13px', fontWeight: 500 }}>{natalName}</span>
                  <span style={{ fontSize: '13px' }}>{emoji}</span>
                </div>
                <span style={{ color: '#888', fontSize: '12px', lineHeight: 1.4, flex: 1, minWidth: '150px' }}>
                  {desc}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </CollapsibleCard>
  );
};

export default DashboardTransits;
